/**
 * VehicleTrackers
 * Live vehicle tracking map — polls every 30 seconds.
 * Only shows vehicles matched to this system's vehicles table.
 * Requires REACT_APP_GOOGLE_MAPS_API_KEY in environment.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Spinner, Form, Badge } from 'react-bootstrap';
import { getLivePositions } from '../../api/integrations';

const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

const VEHICLE_TYPE_ORDER = {
    UNIT: 0,
    RIGID: 1,
    TRAILER: 2,
};

const VEHICLE_TYPE_LABELS = {
    UNIT: 'Units',
    RIGID: 'Rigids',
    TRAILER: 'Trailers',
};

function esc(s) {
    return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function vehicleTypeColor(vehicleType) {
    switch ((vehicleType || '').toUpperCase()) {
        case 'UNIT': return '#9333ea';
        case 'RIGID': return '#1997eb';
        case 'TRAILER': return '#ea3333';
        default: return '#4b5563';
    }
}

function formatSpeed(speed) {
    const numericSpeed = Number(speed);
    return Number.isFinite(numericSpeed) ? Math.round(numericSpeed) : '—';
}

// Compact teardrop pin — colour-coded by vehicle category.
function pinMarkerSvg(vehicleType) {
    const bg = vehicleTypeColor(vehicleType);
    return `data:image/svg+xml;charset=UTF-8,` + encodeURIComponent(`
        <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <filter id="sh" x="-40%" y="-20%" width="180%" height="160%">
                    <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/>
                </filter>
            </defs>
            <g filter="url(#sh)">
                <path d="M14 2 C8 2 3 7 3 13 C3 21 14 34 14 34 C14 34 25 21 25 13 C25 7 20 2 14 2 Z" fill="${bg}"/>
                <circle cx="14" cy="13" r="5" fill="white" opacity="0.9"/>
            </g>
        </svg>
    `);
}

// Trailer marker — simple circle, colour-coded for provider
function trailerMarkerSvg() {
    const color = '#ea3333';

    return `data:image/svg+xml;charset=UTF-8,` + encodeURIComponent(`
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <circle
                cx="10"
                cy="10"
                r="6"
                fill="white"
                stroke="${color}"
                stroke-width="5"
            />
        </svg>
    `);
}

// Full pill label — shown only for the selected vehicle
function selectedMarkerSvg(reg, vehicleType) {
    const bg = vehicleTypeColor(vehicleType);
    const text = esc(reg || 'UNKNOWN');
    return `data:image/svg+xml;charset=UTF-8,` + encodeURIComponent(`
        <svg width="132" height="44" viewBox="0 0 132 44" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <filter id="shadow" x="-20%" y="-30%" width="140%" height="180%">
                    <feDropShadow dx="0" dy="1.5" stdDeviation="2" flood-color="rgba(0,0,0,0.5)"/>
                </filter>
            </defs>
            <g filter="url(#shadow)">
                <rect x="6" y="2" width="120" height="26" rx="13" fill="${bg}"/>
                <rect x="7" y="3" width="118" height="24" rx="12" fill="none" stroke="white" stroke-width="1.5" opacity="0.5"/>
                <text x="66" y="19" text-anchor="middle" font-family="Segoe UI, Arial, sans-serif" font-size="13" font-weight="700" fill="white">${text}</text>
                <path d="M66 42 L58 28 L74 28 Z" fill="${bg}"/>
            </g>
        </svg>
    `);
}

export default function VehicleTrackers({ theme, vehicles, features }) {
    const mapRef          = useRef(null);
    const mapInstance     = useRef(null);
    const markersRef      = useRef({});   // { vehicle_id: google.maps.Marker }
    const infoWindowRef   = useRef(null);
    const infoPinnedRef   = useRef(false); // true when info window was opened by click
    const pollTimer       = useRef(null);
    const userInteracted  = useRef(false); // true once user pans/zooms manually
    const positionDataRef = useRef({});    // { vehicle_id: latest position object }

    const [positions, setPositions]       = useState([]);
    const [loading, setLoading]           = useState(true);
    const [lastUpdated, setLastUpdated]   = useState(null);
    const [error, setError]               = useState(null);
    const [filterVehicleId, setFilterVehicleId] = useState('');
    const [countdown, setCountdown]       = useState(POLL_INTERVAL_MS / 1000);
    const countdownRef = useRef(countdown);
    countdownRef.current = countdown;

    const filteredVehicles = useMemo(() => {
        return (vehicles || [])
            .filter(v => {
                const type = (v.type || '').toUpperCase();
                const status = (v.status || '').toLowerCase();
                return (type === 'UNIT' || type === 'RIGID' || type === 'TRAILER') && status === 'active';
            })
            .sort((a, b) => {
                const typeCompare =
                    (VEHICLE_TYPE_ORDER[(a.type || '').toUpperCase()] ?? 999) -
                    (VEHICLE_TYPE_ORDER[(b.type || '').toUpperCase()] ?? 999);

                if (typeCompare !== 0) return typeCompare;

                return (a.reg || '').localeCompare(b.reg || '');
            });        
    }, [vehicles]);

    // const filteredVehicles = useMemo(() => {
    //     const typeOrder = {
    //         UNIT: 0,
    //         RIGID: 1,
    //         TRAILER: 2,
    //     };

    //     return (vehicles || [])
    //         .filter(v => {
    //             const type = (v.type || '').toUpperCase();
    //             const status = (v.status || '').toLowerCase();

    //             return (
    //                 (type === 'UNIT' || type === 'RIGID' || type === 'TRAILER') &&
    //                 status === 'active'
    //             );
    //         })
    //         .sort((a, b) => {
    //             const typeCompare =
    //                 (typeOrder[(a.type || '').toUpperCase()] ?? 999) -
    //                 (typeOrder[(b.type || '').toUpperCase()] ?? 999);

    //             if (typeCompare !== 0) {
    //                 return typeCompare;
    //             }

    //             return (a.reg || '').localeCompare(b.reg || '');
    //         });
    // }, [vehicles]);

    // Build dropdown options from currently tracked positions + provider source
    const dropdownOptions = useMemo(() => {
        const byId = new Map();
        positions.forEach(p => {
            const id = String(p.vehicle_id);
            const existing = byId.get(id) || {
                vehicle_id: p.vehicle_id,
                reg: p.vehicle_registration || `Vehicle #${p.vehicle_id}`,
                providers: new Set(),
            };
            const provider = (p.integration_provider || '').toLowerCase();
            if (provider === 'radius' || provider === 'velocity') existing.providers.add('Velocity');
            else if (provider === 'fleetclear') existing.providers.add('Fleetclear');
            else if (p.integration_name) existing.providers.add(p.integration_name);
            byId.set(id, existing);
        });

        // Keep only active UNIT/RIGID/TRAILER vehicles from our vehicles table
        const vehiclesById = new Map(filteredVehicles.map(v => [String(v.id), v]));
        return Array.from(byId.values())
            .map(v => {
                const vehicle = vehiclesById.get(String(v.vehicle_id));
                return vehicle
                    ? {
                        ...v,
                        type: (vehicle.type || '').toUpperCase(),
                        reg: vehicle.reg || v.reg,
                    }
                    : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
                const typeCompare =
                    (VEHICLE_TYPE_ORDER[a.type] ?? 999) -
                    (VEHICLE_TYPE_ORDER[b.type] ?? 999);

                if (typeCompare !== 0) return typeCompare;

                return (a.reg || '').localeCompare(b.reg || '');
            });
    }, [positions, filteredVehicles]);

    const groupedDropdownOptions = useMemo(() => {
        return dropdownOptions.reduce((groups, vehicle) => {
            if (!groups[vehicle.type]) groups[vehicle.type] = [];
            groups[vehicle.type].push(vehicle);
            return groups;
        }, {});
    }, [dropdownOptions]);

    const orderedDropdownGroups = useMemo(() => {
        return Object.keys(VEHICLE_TYPE_ORDER)
            .map(type => ({ type, options: groupedDropdownOptions[type] || [] }))
            .filter(group => group.options.length > 0);
    }, [groupedDropdownOptions]);

    // Latest data for the selected vehicle — drives the info card
    const selectedVehicleData = useMemo(() => {
        if (!filterVehicleId) return null;
        return positions.find(p => String(p.vehicle_id) === String(filterVehicleId)) || null;
    }, [filterVehicleId, positions]);

    const enabledProviders = useMemo(() => {
        const list = [];
        if (features?.velocity === true) list.push('radius');
        if (features?.fleetclear === true) list.push('fleetclear');
        if (features?.fleetview === true) list.push('fleetview');
        return list;
    }, [features]);

    // ── Google Maps loader ───────────────────────────────────────────────────
    const loadGoogleMaps = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window.google?.maps) { resolve(); return; }
            // Check if script is already in DOM (e.g. loaded by LocationMapModal)
            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                // Wait for it to finish loading
                const wait = setInterval(() => {
                    if (window.google?.maps) { clearInterval(wait); resolve(); }
                }, 100);
                setTimeout(() => { clearInterval(wait); reject(new Error('Google Maps load timeout')); }, 10000);
                return;
            }
            const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!key) { reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY not set')); return; }
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
            script.async = true;
            script.onload  = resolve;
            script.onerror = () => reject(new Error('Failed to load Google Maps'));
            document.head.appendChild(script);
        });
    }, []);

    // ── Initialise map ───────────────────────────────────────────────────────
    const initMap = useCallback(async () => {
        await loadGoogleMaps();
        if (!mapRef.current || mapInstance.current) return;
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center:    { lat: 52.5, lng: -1.5 },  // centre of England
            zoom:      7,
            mapTypeId: 'roadmap',
        });
        infoWindowRef.current = new window.google.maps.InfoWindow();
        // When user closes the pinned info window, unpin it
        infoWindowRef.current.addListener('closeclick', () => {
            infoPinnedRef.current = false;
        });
        // Mark as user-interacted on any manual pan or zoom
        ['dragstart', 'zoom_changed'].forEach(evt =>
            mapInstance.current.addListener(evt, () => { userInteracted.current = true; })
        );
    }, [loadGoogleMaps]);

    // ── Update markers on map ────────────────────────────────────────────────
    // selectedVehicleId — gets the pill-label marker; all others get a pin
    const updateMarkers = useCallback((data, selectedVehicleId) => {
        if (!mapInstance.current || !window.google?.maps) return;

        const validPositions = (data || []).filter(v => {
            const lat = parseFloat(v.lat);
            const lon = parseFloat(v.lon);
            return Number.isFinite(lat) && Number.isFinite(lon);
        });

        const active = new Set();

        validPositions.forEach(v => {
            const key = String(v.vehicle_id);
            active.add(key);

            // Keep fresh data so hover/click handlers always show latest info
            positionDataRef.current[key] = v;

            const lat = parseFloat(v.lat);
            const lon = parseFloat(v.lon);
            const pos   = { lat, lng: lon };
            const label = v.vehicle_registration || `#${v.vehicle_id}`;
            const isSelected = selectedVehicleId && String(v.vehicle_id) === String(selectedVehicleId);

            const title = label; // minimal native tooltip — custom InfoWindow handles details
            const isTrailer = (v.vehicle_type || '').toUpperCase() === 'TRAILER';

            const icon = isSelected
                ? {
                    url:        isTrailer ? trailerMarkerSvg() : selectedMarkerSvg(label, v.vehicle_type),
                    scaledSize: new window.google.maps.Size(132, 44),
                    anchor:     new window.google.maps.Point(66, 42),
                }
                : {
                    url:        isTrailer ? trailerMarkerSvg() : pinMarkerSvg(v.vehicle_type),
                    scaledSize: new window.google.maps.Size(28, 36),
                    anchor:     new window.google.maps.Point(14, 34),
                };

            if (markersRef.current[key]) {
                // Update existing marker
                markersRef.current[key].setPosition(pos);
                markersRef.current[key].setTitle(title);
                markersRef.current[key].setIcon(icon);
            } else {
                // Create new marker with hover + click behaviour
                const marker = new window.google.maps.Marker({
                    position: pos,
                    map:      mapInstance.current,
                    title,
                    icon,
                });

                const buildHoverContent = (d) => {
                    const color = vehicleTypeColor(d.vehicle_type);
                    return `
                        <div style="min-width:160px;font-family:sans-serif;font-size:12px;line-height:1.6;border-left:3px solid ${color};padding-left:8px">
                            <strong style="font-size:14px">${esc(d.vehicle_registration || label)}</strong><br/>
                            ${d.driver_name ? `<span style="color:#555">${esc(d.driver_name)}</span><br/>` : ''}
                            <span>Speed: ${formatSpeed(d.speed)} ${d.speed_unit || 'MPH'}</span><br/>
                            <span>Ignition: ${d.ignition ? '🟢 On' : '⚫ Off'}</span>
                        </div>
                    `;
                };

                const buildClickContent = (d) => {
                    const color = vehicleTypeColor(d.vehicle_type);
                    const ts = d.timestamp ? new Date(parseInt(d.timestamp, 10) * 1000).toLocaleTimeString('en-GB') : '—';
                    return `
                        <div style="min-width:200px;font-family:sans-serif;font-size:13px;line-height:1.6;border-left:4px solid ${color};padding-left:10px">
                            <strong style="font-size:15px">${esc(d.vehicle_registration || label)}</strong><br/>
                            ${d.driver_name ? `<span style="color:#555">Driver: ${esc(d.driver_name)}</span><br/>` : ''}
                            ${d.integration_provider ? `<span style="color:${color};font-weight:600">${esc(d.integration_provider)}</span><br/>` : ''}
                            <span>Speed: ${formatSpeed(d.speed)} ${d.speed_unit || 'MPH'}</span><br/>
                            <span>Ignition: ${d.ignition ? '🟢 On' : '⚫ Off'}</span><br/>
                            ${d.street    ? `<span>${esc(d.street)}</span><br/>`    : ''}
                            ${d.post_code ? `<span>${esc(d.post_code)}</span><br/>` : ''}
                            <span style="color:#999;font-size:11px">Updated: ${ts}</span>
                        </div>
                    `;
                };

                // Hover: show brief tooltip, but don't override a pinned (clicked) window
                marker.addListener('mouseover', () => {
                    if (infoPinnedRef.current) return;
                    const d = positionDataRef.current[key] || v;
                    infoWindowRef.current.setContent(buildHoverContent(d));
                    infoWindowRef.current.open(mapInstance.current, marker);
                });

                marker.addListener('mouseout', () => {
                    if (infoPinnedRef.current) return;
                    infoWindowRef.current.close();
                });

                // Click: show full details, pin the window open
                marker.addListener('click', () => {
                    infoPinnedRef.current = true;
                    const d = positionDataRef.current[key] || v;
                    infoWindowRef.current.setContent(buildClickContent(d));
                    infoWindowRef.current.open(mapInstance.current, marker);
                });

                markersRef.current[key] = marker;
            }
        });

        // Remove markers for vehicles no longer in the result
        Object.keys(markersRef.current).forEach(key => {
            if (!active.has(key)) {
                markersRef.current[key].setMap(null);
                delete markersRef.current[key];
            }
        });

        // Pan to selected vehicle; otherwise auto-fit on first load only
        if (selectedVehicleId) {
            const sel = validPositions.find(v => String(v.vehicle_id) === String(selectedVehicleId));
            if (sel) {
                mapInstance.current.panTo({ lat: parseFloat(sel.lat), lng: parseFloat(sel.lon) });
                mapInstance.current.setZoom(14);
            }
        } else if (!userInteracted.current && validPositions.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            validPositions.forEach(v => bounds.extend({ lat: parseFloat(v.lat), lng: parseFloat(v.lon) }));
            mapInstance.current.fitBounds(bounds);
            if (validPositions.length === 1) mapInstance.current.setZoom(14);
        }
    }, []);

    // ── Fetch positions ──────────────────────────────────────────────────────
    const fetchPositions = useCallback(async () => {
        if (!enabledProviders.length) {
            setPositions([]);
            setError(null);
            return [];
        }

        const r = await getLivePositions(undefined, enabledProviders);
        if (r.ok) {
            setPositions(r.data.vehicles || []);
            setLastUpdated(new Date());
            setError(null);
            return r.data.vehicles || [];
        } else {
            setError(r.data?.error || 'Failed to fetch positions');
            return [];
        }
    }, [enabledProviders]);

    // ── Poll loop ────────────────────────────────────────────────────────────
    const startPolling = useCallback(() => {
        if (pollTimer.current) clearInterval(pollTimer.current);
        setCountdown(POLL_INTERVAL_MS / 1000);

        pollTimer.current = setInterval(async () => {
            await fetchPositions();
            setCountdown(POLL_INTERVAL_MS / 1000);
        }, POLL_INTERVAL_MS);
    }, [fetchPositions]);

    // ── Countdown ticker ─────────────────────────────────────────────────────
    useEffect(() => {
        const tick = setInterval(() => {
            setCountdown(c => (c > 0 ? c - 1 : 0));
        }, 1000);
        return () => clearInterval(tick);
    }, []);

    // ── Boot ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                await initMap();
                if (!mounted) return;
                setLoading(true);
                await fetchPositions();
                if (mounted) {
                    setLoading(false);
                    startPolling();
                }
            } catch (e) {
                if (mounted) { setLoading(false); setError(e.message); }
            }
        })();
        return () => {
            mounted = false;
            if (pollTimer.current) clearInterval(pollTimer.current);
        };
    }, [initMap, fetchPositions, startPolling]);

    // ── Sync markers whenever positions or selection changes ────────────────
    useEffect(() => {
        if (loading) return;
        updateMarkers(positions, filterVehicleId);
    }, [loading, positions, filterVehicleId, updateMarkers]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const inputStyle = {
        backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
        color:           theme === 'dark' ? '#eee'    : '#000',
        borderColor:     theme === 'dark' ? '#555'    : '#ccc',
        maxWidth: '280px',
    };

    const cardStyle = {
        background:   theme === 'dark' ? '#1e293b' : '#f8faff',
        border:       `1px solid ${theme === 'dark' ? '#334155' : '#cbd5e1'}`,
        borderRadius: '10px',
        padding:      '14px 18px',
        fontSize:     '13px',
        lineHeight:   '1.7',
    };

    const fmtCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Toolbar */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                padding: '10px 0 12px',
            }}>
                <Form.Select
                    size="sm"
                    value={filterVehicleId}
                    onChange={e => setFilterVehicleId(e.target.value)}
                    style={{ ...inputStyle, maxWidth: '340px' }}
                >
                    <option value="">All tracked vehicles</option>
                    {orderedDropdownGroups.flatMap(({ type, options: group }, groupIndex) => {
                        const vehicleOptions = group.map(v => (
                            <option key={v.vehicle_id} value={v.vehicle_id}>
                                {v.reg} {v.providers.size ? `(${Array.from(v.providers).join('/')})` : ''}
                            </option>
                        ));

                        return groupIndex === 0
                            ? vehicleOptions
                            : [
                                <option key={`${type}-separator`} value="" disabled>
                                    ── {VEHICLE_TYPE_LABELS[type]} ──
                                </option>,
                                ...vehicleOptions,
                            ];
                    })}
                </Form.Select>

                <span style={{ fontSize: '12px', opacity: 0.6 }}>
                    Key: Units / Rigids / Trailers
                </span>

                <span style={{ fontSize: '13px', opacity: 0.6 }}>
                    {loading
                        ? 'Updating…'
                        : lastUpdated
                            ? `Updated ${lastUpdated.toLocaleTimeString('en-GB')} — next in ${fmtCountdown(countdown)}`
                            : ''}
                </span>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {positions.length > 0 && (
                        <Badge bg="primary" style={{ fontSize: '12px' }}>
                            {positions.length} vehicle{positions.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Selected vehicle info card */}
            {selectedVehicleData && (() => {
                const d = selectedVehicleData;
                const color = vehicleTypeColor(d.vehicle_type);
                const ts = d.timestamp
                    ? new Date(parseInt(d.timestamp, 10) * 1000).toLocaleTimeString('en-GB')
                    : '—';
                return (
                    <div style={{ ...cardStyle, borderLeft: `4px solid ${color}`, marginBottom: '10px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: '18px', fontWeight: 700 }}>{d.vehicle_registration}</div>
                            {d.driver_name && <div style={{ opacity: 0.7 }}>{d.driver_name}</div>}
                            {d.integration_provider && (
                                <div style={{ color, fontWeight: 600, fontSize: '12px' }}>{d.integration_provider}</div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ opacity: 0.5, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Speed</div>
                                <div style={{ fontWeight: 600 }}>{formatSpeed(d.speed)} {d.speed_unit || 'MPH'}</div>
                            </div>
                            <div>
                                <div style={{ opacity: 0.5, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ignition</div>
                                <div style={{ fontWeight: 600 }}>{d.ignition ? '🟢 On' : '⚫ Off'}</div>
                            </div>
                            {(d.street || d.post_code) && (
                                <div>
                                    <div style={{ opacity: 0.5, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</div>
                                    <div>{[d.street, d.post_code].filter(Boolean).join(', ')}</div>
                                </div>
                            )}
                            <div>
                                <div style={{ opacity: 0.5, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Updated</div>
                                <div style={{ opacity: 0.7 }}>{ts}</div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {error && (
                <div style={{ padding: '8px 12px', background: 'rgba(220,53,69,0.12)', borderRadius: '6px', color: '#dc3545', fontSize: '13px', marginBottom: '10px' }}>
                    ⚠ {error}
                </div>
            )}

            {/* Map container */}
            <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}` }}>
                {loading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', zIndex: 10, borderRadius: '8px' }}>
                        <Spinner animation="border" variant="light" />
                    </div>
                )}
                <div ref={mapRef} style={{ width: '100%', height: 'calc(100vh - 310px)', minHeight: '420px' }} />
            </div>

            {/* Vehicle list below map — all vehicles as clickable pills */}
            {positions.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {positions.map(v => (
                        <div
                            key={v.vehicle_id}
                            style={{
                                padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                                background: theme === 'dark' ? '#2a2a2a' : '#f0f0f0',
                                border: `1px solid ${vehicleTypeColor(v.vehicle_type)}`,
                                cursor: 'pointer',
                                outline: filterVehicleId === String(v.vehicle_id) ? `2px solid ${vehicleTypeColor(v.vehicle_type)}` : 'none',
                            }}
                            onClick={() => {
                                const marker = markersRef.current[String(v.vehicle_id)];
                                if (marker && mapInstance.current) {
                                    mapInstance.current.panTo(marker.getPosition());
                                    mapInstance.current.setZoom(14);
                                    window.google.maps.event.trigger(marker, 'click');
                                }
                            }}
                        >
                            <strong>{v.vehicle_registration}</strong>
                            {v.driver_name && <span style={{ opacity: 0.7, marginLeft: '6px' }}>{v.driver_name}</span>}
                            <span style={{ marginLeft: '8px', color: vehicleTypeColor(v.vehicle_type) }}>
                                {v.ignition ? '🟢' : '⚫'} {formatSpeed(v.speed)} {v.speed_unit}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
